/**
 * Логика загрузки 3D моделей
 */

import { useGLTF } from '@react-three/drei';
import { LOAD_TIMEOUTS } from './constants.js';

/**
 * Загружает одну 3D модель с таймаутом
 * @param {string} path 
 * @param {number} timeoutMs 
 * @returns {Promise<string>}
 */
export function loadModelAsync(path, timeoutMs = LOAD_TIMEOUTS.CRITICAL) {
    return new Promise(function loadPromise(resolve, reject) {
        const controller = new AbortController();
        const timer = setTimeout(function timeout() {
            controller.abort();
        }, timeoutMs);

        try {
            useGLTF.preload(path);

            fetch(path, { signal: controller.signal })
                .then(function handleResponse(response) {
                    clearTimeout(timer);
                    if (response.ok) {
                        resolve(path);
                    } else {
                        throw new Error(`HTTP ${response.status}`);
                    }
                })
                .catch(function handleError(error) {
                    clearTimeout(timer);
                    if (process.env.NODE_ENV === 'development') {
                        console.warn(`⚠️ Ошибка загрузки модели ${path}:`, error.message || error);
                    }
                    reject(error);
                });
        } catch (error) {
            clearTimeout(timer);
            if (process.env.NODE_ENV === 'development') {
                console.warn(`⚠️ Ошибка предзагрузки модели ${path}:`, error.message || error);
            }
            reject(error);
        }
    });
}

/**
 * Загружает критические модели с обработкой ошибок
 * @param {string[]} paths 
 * @returns {Promise<boolean>} 
 */
export async function loadCriticalModelsAsync(paths) {
    try {
        const promises = paths.map(function mapPath(path) {
            return loadModelAsync(path);
        });

        const results = await Promise.allSettled(promises);

        const failed = results.filter(function filterFailed(r) {
            return r.status === 'rejected';
        });

        if (failed.length && process.env.NODE_ENV === 'development') {
            console.warn(
                '⚠️ Критические модели не все загрузились:',
                failed.map(function mapReason(f) {
                    return f.reason?.message || f.reason;
                })
            );
        }

        return true;
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.error('❌ Ошибка загрузки критических моделей:', error);
        }
        return true;
    }
}

/**
 * Загружает дополнительные модели в фоновом режиме
 * @param {string[]} paths -
 * @returns {Promise<void>}
 */
export async function loadAdditionalModelsAsync(paths) {
    try {
        const promises = paths.map(function mapPath(path) {
            return loadModelAsync(path, LOAD_TIMEOUTS.ADDITIONAL);
        });

        await Promise.allSettled(promises);
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Некоторые дополнительные модели не загрузились:', error);
        }
    }
}
